import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "@/lib/router";
import { useLang } from "@/context/LanguageContext";
import { SEO, SEO_BASE, LOCALE_MAP } from "@/i18n/seo";
import { splitLang, withLang, isLocalizable } from "@/lib/i18nPath";

const NOINDEX = ["/account", "/admin", "/checkout", "/cart", "/club", "/order", "/review", "/reset-password"];
const HREFLANGS = ["it", "en", "de", "es", "fr"];

// hreflang links are managed outside Helmet (Helmet dedupes by href, which would drop x-default).
export const applyHreflang = (urls) => {
  document.querySelectorAll("link[data-hreflang]").forEach((n) => n.remove());
  if (!urls) return;
  [...HREFLANGS, "x-default"].forEach((l) => {
    const el = document.createElement("link");
    el.rel = "alternate";
    el.hreflang = l;
    el.href = urls[l === "x-default" ? "it" : l];
    el.setAttribute("data-hreflang", "1");
    document.head.appendChild(el);
  });
};

const useHreflang = (base, enabled) => {
  useEffect(() => {
    applyHreflang(enabled ? Object.fromEntries(HREFLANGS.map((l) => [l, SEO_BASE + withLang(base, l)])) : null);
  }, [base, enabled]);
};

export const RouteSeo = () => {
  const { pathname } = useLocation();
  const { lang, t } = useLang();
  const { lang: urlLang, base } = splitLang(pathname);
  const privatePath = NOINDEX.some((p) => base === p || base.startsWith(p + "/"));
  let cfg = urlLang && !isLocalizable(base) ? SEO.notFound : SEO[base];
  if (!cfg && (base.startsWith("/shop/") || base.startsWith("/order/"))) cfg = SEO.default;
  // Unknown paths may be Admin catalog pages: the page itself (CatalogPage / NotFound) sets robots + canonical.
  const known = Boolean(cfg) && cfg !== SEO.notFound;
  cfg = cfg || SEO.notFound;
  const noindex = privatePath || cfg === SEO.notFound;
  const title = t(cfg.title);
  const desc = t(cfg.description);
  const url = SEO_BASE + withLang(base, lang);
  useHreflang(base, known && !noindex);

  return (
    <Helmet>
      <html lang={lang} />
      <title>{title}</title>
      <meta name="description" content={desc} />
      {known && !noindex && <link rel="canonical" href={url} />}
      {privatePath && <meta name="robots" content="noindex, nofollow" />}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:locale" content={LOCALE_MAP[lang] || "it_IT"} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={desc} />
    </Helmet>
  );
};
